/*
 * ONE TIMELINE FOR THE WHOLE COMPANY, READ THE WAY A PERSON READS IT.
 *
 * The old record showed one person's slice: Pluto's legal redlines sat on Gali's
 * record, and nine rows in a row read "Re: Pluto/Remotion followup". The rows now
 * come from GET /companies/:id/timeline (PR #156, getRecordTimeline): everyone at
 * the company, one row per Gmail message with `holders` naming each person who
 * holds a copy, `sender` on inbound mail, stage moves with the proposal behind
 * them (`evidence`, `actor`), meeting outcomes, and PandaDoc notifications as
 * `item_type: 'document'`. This module turns those rows into what the frame draws:
 *
 *   - WHO, on every row: "You → Shahar, Gali:", "Shahar:", "inbound" when nobody
 *     can say (one message held by two people, no intro), "Dani → You, Shahar:"
 *     for a warm intro copied onto the person it introduced;
 *   - the FIRST LINE of the message (the signature and the quoted chain folded
 *     away by the old model's cleanBodyFolded), and the subject only once per
 *     thread in a day, with the company-wide message count on its newest row;
 *   - calendar mail (invites, accepts) FOLDED under its meeting, and bodyless
 *     HubSpot tasks folded away (2,541 of them in prod);
 *   - the filter chips (All · Conversations · Calls & meetings · Stage moves ·
 *     Notes) and "Only {name}", with Everyone the default once there are two or
 *     more people;
 *   - DAY GROUPS THAT NEVER CROSS A DAY, and "Showing 200 of 340" while older rows
 *     wait behind Load older.
 *
 * Pure, so test/record-timeline-model.test.js pins it on a Pluto-shaped fixture.
 */
import { cleanBodyFolded, summarize, normalizeSubject, callActionItems, gmailThreadUrl } from "../timelineModel.mjs";
import { clock, toDate, shortDate, plural, firstName, fullName, dateRange } from "./format.mjs";
import { STAGE_LABELS } from "./situation.mjs";

/* ------------------------------------------------------------ the folds */

/*
 * Google Calendar's own subject prefixes: src/db/mailFilters.js
 * CALENDAR_NOTICE_SUBJECTS, without the SQL wildcard. Copied because the client
 * cannot import from src/; the test reads both files and fails when they drift.
 */
export const CALENDAR_NOTICE_PREFIXES = [
  "invitation:",
  "updated invitation:",
  "accepted:",
  "declined:",
  "tentative:",
  "canceled event:",
  "cancelled event:",
  "re: invitation:",
  "re: updated invitation:",
  "re: accepted:",
  "notification:",
];

/*
 * A meeting invite a mail client wrote with no subject to give it away (Outlook's
 * Teams block, Dynamo Oct 1): src/db/mailFilters.js isMeetingInviteBody, copied
 * for the same reason as the prefixes; the test runs both on the same bodies.
 */
const INVITE_LINK = /teams\.microsoft\.com\/(l\/meetup-join|meet\/)|zoom\.us\/j\/|meet\.google\.com\/[a-z]{3}-|webex\.com\//i;
const INVITE_BLOCK_START = /(^|\n)[ \t]*(_{10,}|Microsoft Teams meeting|Join Zoom Meeting|Join (with|on) Google Meet|Join (the )?Webex meeting)/i;

/** An email body that is a meeting invite's join block and nothing else. */
export function isMeetingInviteBody(body) {
  const text = String(body || "");
  if (!INVITE_LINK.test(text)) return false;
  const m = INVITE_BLOCK_START.exec(text);
  return Boolean(m) && !text.slice(0, m.index).trim();
}

/** An email a calendar wrote (an invite, an accept), not a person. */
export function isCalendarMail(row) {
  if (!row || row.subtype !== "email") return false;
  const subject = String(row.title || "").trim().toLowerCase();
  if (subject !== "" && CALENDAR_NOTICE_PREFIXES.some((p) => subject.startsWith(p))) return true;
  return isMeetingInviteBody(row.body);
}

/*
 * A recording Fireflies titled with the join link ("Conference Link:
 * https://teams.microsoft.com/l/meetup-join/19:meeting_…"): named for the tool
 * instead, so a URL does not run across the row.
 */
export function callTitle(title) {
  const t = String(title || "").trim();
  if (!/^(conference link:\s*)?https?:\/\//i.test(t)) return t;
  if (/teams\.microsoft\.com/i.test(t)) return "Teams meeting";
  if (/zoom\.us/i.test(t)) return "Zoom meeting";
  if (/meet\.google\.com/i.test(t)) return "Google Meet";
  return "Video call";
}

/** A HubSpot task with nothing in it: imported furniture, folded away. */
export function isEmptyHubspotTask(row) {
  if (!row || row.subtype !== "task") return false;
  const fromHubspot = /hubspot/i.test(String(row.source || ""));
  return fromHubspot && !String(row.body || "").trim();
}

/* ------------------------------------------------------------ the filters */

export const FILTERS = [
  { id: "all", label: "All" },
  { id: "conversations", label: "Conversations" },
  { id: "calls", label: "Calls & meetings" },
  { id: "stages", label: "Stage moves" },
  { id: "notes", label: "Notes" },
];

const CONVERSATION_TYPES = new Set(["email", "linkedin_message", "whatsapp_message", "form_submission", "slack"]);

/** Which filter chip a row belongs to. */
export function filterOf(row) {
  if (!row) return "all";
  if (row.item_type === "stage_transition" || row.item_type === "document") return "stages";
  if (row.subtype === "call" || row.subtype === "meeting") return "calls";
  if (row.subtype === "note" || row.subtype === "task") return "notes";
  if (CONVERSATION_TYPES.has(row.subtype)) return "conversations";
  return "all";
}

/* ------------------------------------------------------------ who wrote it */

const nameOf = (p) => (p ? firstName(p) || (p.email ? String(p.email).split("@")[0] : null) : null);

function holdersOf(row) {
  return Array.isArray(row && row.holders) ? row.holders : [];
}

/** The people a row is about: its contact, its holders, its sender. */
export function peopleOf(row) {
  const ids = new Set();
  if (row && row.contact_id) ids.add(String(row.contact_id));
  for (const h of holdersOf(row)) if (h && h.contact_id) ids.add(String(h.contact_id));
  if (row && row.sender && row.sender.contact_id) ids.add(String(row.sender.contact_id));
  return ids;
}

/**
 * WHO, as the row's lead-in, without the colon:
 *   outbound        "You → Shahar, Gali"   (every company contact holding a copy)
 *   inbound, known  "Shahar"               (the single holder, or the introducer)
 *   a warm intro    "Dani → You, Shahar"   (the sender is not the holder)
 *   inbound, shared "inbound"              (two holders and no intro: nobody can say)
 *   no direction    "Shahar"  with dirUnknown, for the HubSpot import's mail
 */
export function whoLine(row) {
  const holders = holdersOf(row);
  const names = holders.map(nameOf).filter(Boolean);
  const own = row && (row.contact_first_name || row.contact_last_name) ? nameOf({ first_name: row.contact_first_name, last_name: row.contact_last_name }) : null;
  const direction = row && (row.direction || (row.metadata && row.metadata.direction) || null);
  if (direction === "outbound") {
    const to = names.length ? names : own ? [own] : [];
    return { who: to.length ? `You → ${to.join(", ")}` : "You", direction };
  }
  if (direction === "inbound") {
    const sender = row.sender || null;
    if (sender) {
      const s = nameOf(sender);
      const senderHolds = holders.some((h) => String(h.contact_id) === String(sender.contact_id));
      if (!senderHolds && names.length) return { who: `${s} → You, ${names.join(", ")}`, direction, sender: s };
      return { who: s, direction, sender: s };
    }
    if (holders.length > 1) return { who: "inbound", direction };
    return { who: names[0] || own || "inbound", direction, sender: names[0] || own || null };
  }
  return { who: names.join(", ") || own || "", direction: null, dirUnknown: true };
}

/** The first line of what was written, signature and quote folded away. */
export function firstLine(text, max = 110) {
  const { body } = cleanBodyFolded(text || "");
  const line = String(body || "")
    .split("\n")
    .map((l) => l.trim())
    .find(Boolean);
  if (!line) return "";
  return summarize(line, max);
}

/* ------------------------------------------------------------ stage moves */

const STATUS_WORDS = {
  prospect: "back in the pipeline",
  client: "client",
  churned: "Churned",
  lost: "Closed lost",
  recycled: "Parked",
  not_a_fit: "Not a fit",
  vendor: "Vendor",
  advisory: "Advisory",
};
const LIFECYCLE_WORDS = {
  lead: "lead",
  marketingqualifiedlead: "MQL",
  salesqualifiedlead: "SQL",
  opportunity: "opportunity",
  customer: "customer",
  subscriber: "subscriber",
  other: "other",
};
const SOURCE_READ = {
  calendar: "the calendar invite",
  gmail: "an email",
  gmail_pandadoc: "a PandaDoc email",
  fireflies: "a call",
  webflow_form: "the form",
  landing_page: "the landing page",
  auditor: "the auditor",
};

/*
 * A PERSON'S OWN MOVES (lifecycle, kind, lead status) are People's facts, not the
 * company's milestones. Their arrival (from nothing to "lead", written when the
 * contact is created) is folded away: it is the same moment as the row that
 * brought them. A later move reads "Shahar → customer · set by you".
 */
const isContactMove = (row) => row && row.item_type === "stage_transition" && !!row.contact_id && row.subtype !== "status" && row.subtype !== "pipeline_stage";
export const isArrivalMove = (row) => isContactMove(row) && (row.from_value === null || row.from_value === undefined);

function contactMoveText(row) {
  const who = nameOf({ first_name: row.contact_first_name, last_name: row.contact_last_name }) || "Someone";
  const to = row.subtype === "lifecycle_stage" ? LIFECYCLE_WORDS[row.to_value] || row.to_value : String(row.to_value || "").replace(/_/g, " ");
  const actor = actorWords(row);
  return `${who} → ${to}${actor ? ` · ${actor}` : ""}`;
}

const isCompanyMove = (row) => row && row.item_type === "stage_transition" && (row.subtype === "status" || row.subtype === "pipeline_stage") && !row.contact_id;

/** "from the calendar invite · automatic", "you approved", "set by you". */
export function actorWords(row) {
  const ev = row && row.evidence;
  const from = ev && SOURCE_READ[ev.evidence_source] ? `from ${SOURCE_READ[ev.evidence_source]}` : null;
  switch (row && row.actor) {
    case "you_approved":
      return "you approved";
    case "automatic":
      return [from, "automatic"].filter(Boolean).join(" · ");
    case "by_hand":
      return "set by you";
    case "claude":
      return "set through Claude";
    case "import":
      return "from the HubSpot import";
    default:
      return "";
  }
}

/**
 * The expanded stage move, as the evidence box's lines (reality.txt [7]): where
 * it was read, whether it needed approval, who approved it; on a hand move, the
 * last message before it, never "Evidence". `before` is the newest inbound row
 * older than the move, which the caller passes from the same list.
 */
export function stageEvidence(row, { before = null, now = new Date() } = {}) {
  const lines = [];
  const ev = row && row.evidence;
  if (ev) {
    if (ev.evidence) lines.push(String(ev.evidence));
    const where = SOURCE_READ[ev.evidence_source];
    if (where) lines.push(`Read from ${where}`);
    if (ev.status === "approved") {
      lines.push(`Needed your approval · you approved it${ev.resolved_at ? ` ${shortDate(ev.resolved_at, { now })}` : ""}`);
    } else if (ev.status === "auto_applied") {
      lines.push("Automatic");
    }
  } else if (row && row.actor === "by_hand") {
    if (before) {
      const mins = Math.round((toDate(row.occurred_at) - toDate(before.at)) / 60000);
      const gap = mins >= 0 && mins < 120 ? `, ${plural(mins, "minute")} after ${before.who} wrote` : "";
      lines.push(`Set by hand${gap}`);
      lines.push(`Last message before it: ${before.who}, ${clock(before.at)} “${before.text}”`);
    } else {
      lines.push("Set by hand");
    }
  } else if (row && row.actor === "claude") {
    lines.push("Set through Claude");
  }
  return lines;
}

function stageText(moves, company) {
  const name = (company && company.name) || "The company";
  const stage = moves.find((m) => m.subtype === "pipeline_stage");
  const status = moves.find((m) => m.subtype === "status");
  const parts = [];
  if (stage && STAGE_LABELS[stage.to_value]) parts.push(STAGE_LABELS[stage.to_value]);
  // A win is "Closed won · client" whether or not the stage move came with it.
  if (status && status.to_value === "client" && !parts.includes("Closed won")) parts.push("Closed won");
  if (status && STATUS_WORDS[status.to_value] && !(stage && status.to_value === "prospect")) {
    const w = STATUS_WORDS[status.to_value];
    if (!parts.includes(w)) parts.push(w);
  }
  if (!parts.length && (stage || status)) parts.push(String((stage || status).to_value || "").replace(/_/g, " "));
  const actor = actorWords(status || stage) || actorWords(stage || status);
  return `${name} → ${parts.join(" · ")}${actor ? ` · ${actor}` : ""}`;
}

/* ------------------------------------------------------------ one row */

function kindOf(row) {
  if (row.item_type === "stage_transition") return "stage";
  if (row.item_type === "document") return "document";
  switch (row.subtype) {
    case "email":
      return "email";
    case "meeting":
      return "meeting";
    case "call":
      return "call";
    case "form_submission":
      return "form";
    case "note":
      return "note";
    case "task":
      return "task";
    case "linkedin_message":
    case "whatsapp_message":
      return "dm";
    default:
      return "other";
  }
}

// The row's marker: a ring for a message, a square for a milestone, a diamond for
// a form, an outlined square for a meeting (the frames' own glyphs).
const MARKERS = { email: "ring", dm: "ring", note: "ring", task: "ring", other: "ring", stage: "square", document: "square", form: "diamond", meeting: "box", call: "box" };

const OURS = /gabriel|remotion/i;

/*
 * The to-do line under a call's collapsed row: "Ours: send the case study ·
 * Theirs: confirm Q4 budget", as frame 1d draws it.
 *
 * A COLLAPSED ROW IS ONE LINE. Fireflies writes a real weekly call's action
 * items as a dozen long sentences (DataHub's Aug 20 call: 30 of them), and
 * printing them all made one row a screen tall. So the line spells the items
 * out only while they fit a line; past that it counts them ("Ours: 2 to-dos ·
 * Theirs: 9 to-dos"), and the full lists live in the row's expansion and in
 * the Call held block, which read `ours` and `theirs`, not `line`.
 */
const TODO_LINE_MAX = 110;

export function callTodos(raw) {
  const groups = callActionItems(raw);
  const ours = [];
  const theirs = [];
  for (const g of groups) (g.owner && OURS.test(g.owner) ? ours : theirs).push(...g.items);
  const spelled = [ours.length ? `Ours: ${ours.join(", ")}` : null, theirs.length ? `Theirs: ${theirs.join(", ")}` : null].filter(Boolean).join(" · ");
  const todo = (n) => `${n} to-do${n === 1 ? "" : "s"}`;
  const counted = [ours.length ? `Ours: ${todo(ours.length)}` : null, theirs.length ? `Theirs: ${todo(theirs.length)}` : null].filter(Boolean).join(" · ");
  return { ours, theirs, line: spelled.length <= TODO_LINE_MAX ? spelled : counted };
}

const threadKey = (row) => {
  const id = row && row.metadata && row.metadata.thread_id;
  if (id) return `t:${id}`;
  const s = normalizeSubject(row && row.title);
  return s ? `s:${s.toLowerCase()}` : null;
};

/**
 * One row as the timeline draws it:
 *   { key, id, kind, filter, at, clock, who, text, sub[], marker, milestone,
 *     snippet, thread, meeting?, call?, document?, evidence?, item }
 * `sub` is the grey second line's parts; the caller joins them with " · ".
 */
export function rowView(row, { company = null, now = new Date(), timeZone } = {}) {
  const kind = kindOf(row);
  const base = {
    key: `${row.item_type}-${row.id}`,
    id: row.id,
    kind,
    filter: filterOf(row),
    at: row.occurred_at,
    clock: clock(row.occurred_at, { timeZone }),
    who: "",
    text: "",
    sub: [],
    marker: MARKERS[kind] || "ring",
    milestone: kind === "stage",
    // What joins who and text: "Shahar: done", but "Pluto & Remotion · 16:00–16:30".
    sep: kind === "meeting" || kind === "call" || kind === "document" ? " · " : ": ",
    snippet: false,
    thread: null,
    item: row,
  };
  const meta = row.metadata || {};
  switch (kind) {
    case "email": {
      const w = whoLine(row);
      base.who = w.who;
      base.direction = w.direction;
      base.dirUnknown = !!w.dirUnknown;
      base.text = firstLine(row.body) || normalizeSubject(row.title) || "(no text)";
      base.snippet = meta.body_kind === "snippet";
      base.thread = { key: threadKey(row), subject: row.title || "", id: meta.thread_id || null, url: gmailThreadUrl(meta.thread_id) };
      if (base.snippet) base.sub.push("snippet only");
      if (Array.isArray(meta.cc_emails) && meta.cc_emails.length) base.cc = meta.cc_emails;
      break;
    }
    case "dm": {
      const w = whoLine({ ...row, direction: row.direction || meta.direction || "inbound" });
      base.who = w.who;
      base.text = firstLine(row.body) || (row.subtype === "linkedin_message" ? "LinkedIn message" : "WhatsApp message");
      break;
    }
    case "form": {
      base.who = nameOf({ first_name: row.contact_first_name, last_name: row.contact_last_name }) || "Form";
      const formName = (meta.form && (meta.form.form_name || meta.form.name)) || meta.form_name || row.title || "Website form";
      const message = (meta.form_context && meta.form_context.about) || (meta.form && meta.form.message) || row.body || "";
      base.text = [formName, firstLine(message, 80)].filter(Boolean).join(" · ");
      break;
    }
    case "meeting": {
      base.who = row.title || "Meeting";
      const end = meta.end_at ? clock(meta.end_at, { timeZone }) : null;
      base.text = end ? `${base.clock}–${end}` : base.clock;
      const past = toDate(row.occurred_at) <= toDate(now);
      base.meeting = {
        outcome: row.outcome ? row.outcome.outcome : null,
        past,
        event_id: row.event_id || null,
        attendees: holdersOf(row).map(nameOf).filter(Boolean),
      };
      if (row.outcome) base.sub.push(row.outcome.outcome === "held" ? "Held" : "No-show");
      else if (past) base.sub.push("Not recorded");
      break;
    }
    case "call": {
      const mins = Number(meta.duration_minutes);
      base.who = "Call";
      base.text = [callTitle(row.title), Number.isFinite(mins) && mins > 0 ? `${mins} min` : null, "recorded"].filter(Boolean).join(" · ");
      const todos = callTodos(meta.action_items);
      base.call = { todos, transcript_url: meta.transcript_url || null, summary: row.body || null, minutes: Number.isFinite(mins) ? mins : null };
      if (todos.line) base.sub.push(todos.line);
      break;
    }
    case "note":
      base.who = row.title || "Note";
      base.text = firstLine(row.body) || "";
      break;
    case "task":
      base.who = "Task";
      base.text = row.title || firstLine(row.body) || "";
      break;
    case "document": {
      const viewer = row.viewer_name || null;
      const doc = row.document_name ? `“${row.document_name}”` : "the agreement";
      base.who = "PandaDoc";
      base.text = row.event === "signed" ? `${doc} signed${viewer ? ` by ${viewer}` : ""}` : `${viewer || "Someone"} viewed ${doc}`;
      base.document = { event: row.event, viewer, name: row.document_name || null, ref: row.ref || null };
      break;
    }
    case "stage":
      if (isContactMove(row)) {
        base.text = contactMoveText(row);
        base.milestone = false;
        base.marker = "ring";
      } else {
        base.text = stageText([row], company);
      }
      base.evidence = row.evidence || null;
      break;
    default:
      base.who = row.title || "";
      base.text = firstLine(row.body);
  }
  return base;
}

/* ------------------------------------------------------------ the list */

/**
 * The whole tab.
 *
 *   buildTimeline(rows, { company, contact, filter, only, total, hasMore, now, timeZone })
 *     → { days: [{ key, heading, rows }], filters, people, everyone, onlyOptions,
 *         folded: { calendar, tasks }, entries, colleagues, showing, start }
 *
 * `rows` is the payload's `timeline`, newest first. `only` is a contact id or null.
 * The folds run before the filters, so a hidden calendar invite never counts as an
 * entry, and the counts are computed here, at runtime, from the deduplicated rows
 * (reality.txt [29]: every count on the record is company-wide).
 */
export function buildTimeline(rows, { company = null, contact = null, filter = "all", only = null, total = null, hasMore = false, now = new Date(), timeZone } = {}) {
  const list = (rows || []).filter(Boolean);

  // Calendar mail folds under its meeting: the meeting whose title the notice
  // names, else the nearest meeting in time. A notice with no meeting at all is
  // counted and kept out.
  const meetings = list.filter((r) => r.subtype === "meeting");
  const calendarFor = new Map();
  let calendarOrphans = 0;
  for (const r of list) {
    if (!isCalendarMail(r)) continue;
    const subject = String(r.title || "").toLowerCase();
    let home = meetings.find((m) => m.title && subject.includes(String(m.title).toLowerCase()));
    if (!home && meetings.length) {
      home = meetings.reduce((best, m) =>
        Math.abs(toDate(m.occurred_at) - toDate(r.occurred_at)) < Math.abs(toDate(best.occurred_at) - toDate(r.occurred_at)) ? m : best
      );
    }
    if (home) calendarFor.set(home.id, (calendarFor.get(home.id) || 0) + 1);
    else calendarOrphans += 1;
  }
  const tasks = list.filter(isEmptyHubspotTask).length;
  const arrivals = list.filter(isArrivalMove).length;
  const kept = list.filter((r) => !isCalendarMail(r) && !isEmptyHubspotTask(r) && !isArrivalMove(r));

  // A win writes a status move and a stage move at the same moment, and the frame
  // draws one milestone ("Pluto → Closed won · client · set by you"): company moves
  // within a minute of each other fold into the first.
  const merged = [];
  for (const r of kept) {
    const prev = merged[merged.length - 1];
    if (
      prev && isCompanyMove(r) && isCompanyMove(prev.rows[0]) &&
      Math.abs(toDate(prev.rows[0].occurred_at) - toDate(r.occurred_at)) <= 60000 &&
      !prev.rows.some((x) => x.subtype === r.subtype)
    ) {
      prev.rows.push(r);
      continue;
    }
    merged.push({ rows: [r] });
  }

  // Thread message counts, company-wide, for "Re: … · 9 messages".
  const threadCount = new Map();
  for (const r of kept) {
    if (r.subtype !== "email") continue;
    const k = threadKey(r);
    if (k) threadCount.set(k, (threadCount.get(k) || 0) + 1);
  }

  const views = merged.map(({ rows: group }) => {
    const v = rowView(group[0], { company, now, timeZone });
    if (group.length > 1) {
      v.text = stageText(group, company);
      v.evidence = group.map((g) => g.evidence).find(Boolean) || null;
      v.items = group;
    }
    const folded = calendarFor.get(group[0].id);
    if (folded) v.sub.push(`${plural(folded, "calendar email")} folded`);
    return v;
  });

  // The hand move's "last message before it": the newest inbound older than it.
  views.forEach((v, i) => {
    if (v.kind !== "stage") return;
    const before = views.slice(i + 1).find((x) => x.kind === "email" && x.direction === "inbound");
    v.before = before ? { who: before.who, at: before.at, text: before.text } : null;
    v.evidenceLines = stageEvidence(v.items ? v.items.find((x) => x.actor) || v.item : v.item, { before: v.before, now });
  });

  // Everyone, or one person (company milestones stay: they are the spine).
  const contacts = (company && company.contacts) || [];
  const everyone = contacts.length >= 2;
  const onlyOptions = contacts.map((c) => ({ id: c.id, name: firstName(c) || fullName(c) }));
  const people = (v) => peopleOf(v.item);
  const onlyId = only ? String(only) : null;
  const visible = views.filter((v) => {
    if (filter && filter !== "all" && v.filter !== filter) return false;
    if (onlyId && v.kind !== "stage" && v.kind !== "document" && !people(v).has(onlyId)) return false;
    return true;
  });

  // Day groups, in the reader's zone, never crossing a day: the subject is printed
  // once per thread per day, and the message count only on a thread's newest row.
  const days = [];
  const seenThread = new Set();
  let current = null;
  for (const v of visible) {
    const dayKey = toDate(v.at) ? toDate(v.at).toLocaleDateString("en-CA", timeZone ? { timeZone } : undefined) : "unknown";
    if (!current || current.key !== dayKey) {
      current = { key: dayKey, heading: dayHeading(v.at, { now, timeZone }), rows: [], threads: new Set() };
      days.push(current);
    }
    if (v.thread && v.thread.key) {
      if (!current.threads.has(v.thread.key)) {
        current.threads.add(v.thread.key);
        const count = threadCount.get(v.thread.key) || 1;
        const first = !seenThread.has(v.thread.key);
        seenThread.add(v.thread.key);
        if (v.thread.subject) v.sub.unshift(first && count > 1 ? `${v.thread.subject} · ${plural(count, "message")}` : v.thread.subject);
      }
    }
    current.rows.push(v);
  }
  for (const d of days) delete d.threads;

  const thisId = contact ? String(contact.id) : null;
  // From colleagues' records: rows someone else at the company holds. A company's
  // own note (no person on it) is nobody's record, so it does not count.
  const colleagues = thisId
    ? views.filter((v) => v.kind !== "stage" && v.kind !== "document" && people(v).size > 0 && !people(v).has(thisId)).length
    : 0;
  const entries = views.length;
  const all = total !== null && total !== undefined ? Number(total) : null;
  const showing = hasMore && all ? `Showing ${list.length} of ${all}` : null;
  const start = !hasMore
    ? `Start of the record · ${plural(entries, "entry", "entries")}${colleagues ? `, ${colleagues} of them from colleagues’ records` : ""}`
    : null;

  return {
    days,
    filters: FILTERS.map((f) => ({ ...f, count: f.id === "all" ? views.length : views.filter((v) => v.filter === f.id).length })),
    people: contacts.length,
    everyone,
    onlyOptions,
    folded: { calendar: calendarOrphans + [...calendarFor.values()].reduce((s, n) => s + n, 0), calendarOrphans, tasks, arrivals },
    entries,
    colleagues,
    showing,
    start,
    header: everyone && company ? `Everyone at ${company.name} · ${plural(contacts.length, "person", "people")} · ${plural(entries, "entry", "entries")}` : null,
  };
}

/** "Today, Oct 1", "Sep 30", "Mar 3, 2025": the day header. */
export function dayHeading(ts, { now = new Date(), timeZone } = {}) {
  const d = toDate(ts);
  if (!d) return "";
  const key = (x) => x.toLocaleDateString("en-CA", timeZone ? { timeZone } : undefined);
  const day = shortDate(d, { now, timeZone });
  if (key(d) === key(toDate(now))) return `Today, ${day}`;
  const y = new Date(toDate(now).getTime() - 86400000);
  if (key(d) === key(y)) return `Yesterday, ${day}`;
  return day;
}

/** The newest human conversation in the rows: what Latest conversation shows. */
export function latestConversation(rows, { now = new Date() } = {}) {
  const mail = (rows || []).filter(
    (r) => r && r.subtype === "email" && !isCalendarMail(r) && !(r.metadata && r.metadata.bulk)
  );
  if (!mail.length) return null;
  const newest = mail[0];
  const lastInbound = mail.find((r) => (r.direction || (r.metadata || {}).direction) === "inbound") || null;
  const lastOutbound = mail.find((r) => (r.direction || (r.metadata || {}).direction) === "outbound") || null;
  // The thread the card is about: the one their last message is on (the card leads
  // with it), else the newest.
  const anchor = lastInbound || newest;
  const key = threadKey(anchor);
  const thread = key ? mail.filter((r) => threadKey(r) === key) : [anchor];
  return {
    newest: rowView(newest, { now }),
    lastInbound: lastInbound ? rowView(lastInbound, { now }) : null,
    lastOutbound: lastOutbound ? rowView(lastOutbound, { now }) : null,
    thread: {
      subject: anchor.title || "",
      count: thread.length,
      range: dateRange(thread[thread.length - 1].occurred_at, thread[0].occurred_at, { now }),
      url: gmailThreadUrl((anchor.metadata || {}).thread_id),
    },
  };
}
